import { useState } from 'react';
import { Badge } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useChartingRun } from '../hooks/useChartingRun';
import { useChartingRuns } from '../hooks/useChartingRuns';
import type { ChartingRunSummary } from '../api/payersApi';
import styles from './ChartingCallsCard.module.css';

function assemblyBadge(status: ChartingRunSummary['assemblyStatus']) {
  if (status === 'ok') return <Badge variant="success">assembled ok</Badge>;
  if (status === 'partial') return <Badge variant="warning">partial</Badge>;
  return <Badge variant="error">failed</Badge>;
}

function unclassifiableCount(notes: string | null): number {
  if (!notes) return 0;
  return (notes.match(/unclassifiable/gi) ?? []).length;
}

function ChartingCallRow({
  run,
  label,
  expanded,
  onToggle,
}: {
  run: ChartingRunSummary;
  label: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { run: detail } = useChartingRun(expanded ? run.id : null);
  const unclassifiable = unclassifiableCount(run.assemblyNotes);

  return (
    <>
      <div className={styles.callRow} onClick={onToggle}>
        {assemblyBadge(run.assemblyStatus)}
        <span className={styles.grow}>
          <b>{label}</b> · terminal: <b>{run.terminalState}</b>
          {unclassifiable > 0 && (
            <>
              {' '}
              <Badge variant="warning" size="sm">
                {unclassifiable} unclassifiable input{unclassifiable === 1 ? '' : 's'}
              </Badge>
            </>
          )}
        </span>
        <span className={styles.rowDate}>{new Date(run.createdAt).toLocaleString()}</span>
      </div>
      {expanded && detail && (
        <div className={styles.detail}>
          {detail.assemblyNotes && <p className={styles.detailNote}>{detail.assemblyNotes}</p>}
          {detail.transcript ? (
            <div className={styles.transcript}>{detail.transcript}</div>
          ) : (
            <p className={styles.detailNote}>No transcript recorded for this call.</p>
          )}
          {detail.recordingUrl && (
            <p className={styles.detailNote}>
              <a href={detail.recordingUrl} target="_blank" rel="noreferrer">
                Call recording
              </a>
            </p>
          )}
        </div>
      )}
    </>
  );
}

// Collapsed debugging log of every charting call placed for this payer —
// independent of which map version is selected above, since a call can be
// worth inspecting (a bad transcript, an unclassifiable-input note) even
// once its version has been superseded.
export function ChartingCallsCard({ payerId }: { payerId: string }) {
  const { runs, isLoading } = useChartingRuns(payerId);
  const [expanded, setExpanded] = useState(false);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  if (isLoading) return null;

  return (
    <div>
      <div className={styles.header} onClick={() => setExpanded((e) => !e)}>
        <span className={styles.headerLabel}>
          Charting calls <span className={styles.headerHint}>· {runs.length} · debugging</span>
        </span>
        <span className={styles.toggleLink}>{expanded ? 'hide' : 'show'} ▾</span>
      </div>

      {expanded &&
        (runs.length === 0 ? (
          <div className={patterns.gridTableEmpty}>
            No charting calls placed for this payer yet.
          </div>
        ) : (
          runs.map((run, i) => (
            <ChartingCallRow
              key={run.id}
              run={run}
              label={`Run ${runs.length - i}`}
              expanded={openRunId === run.id}
              onToggle={() => setOpenRunId((id) => (id === run.id ? null : run.id))}
            />
          ))
        ))}
    </div>
  );
}
